import { Component, DestroyRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { API, ColorDefinitionDTO } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { UtilService } from '../../../../services/util.service';
import { ColorPickerService } from '../../../../services/color-picker.service';
import { ErrorService } from '../../../../services/error.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-edit-color',
  templateUrl: './color-edit.component.html',
  styleUrls: ['./color-edit.component.scss'],
  standalone: false,
})
export class ColorEditComponent implements OnInit {
  colorID: number;
  loading: boolean;
  color: ColorDefinitionDTO;

  form = new FormGroup({
    Color: new FormControl('', [Validators.required]),
    Name: new FormControl('', [Validators.required]),
    Description: new FormControl(''),
  });

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    public colorService: ColorPickerService,
    private errorService: ErrorService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.colorID = null;
    this.color = null;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.colorID = params.colorID ? +params.colorID : null;
      this.setupView();
      this.registerFormChangesListener();
    });
  }

  private registerFormChangesListener() {
    this.Color.valueChanges.subscribe(value => {
      if (value?.length === 6) {
        this.color.color = value;
      }
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Color() {
    return this.form.get('Color');
  }

  private setupView() {
    if (this.colorID !== null && this.colorID !== undefined) {
      this.api
        .shared_ColorDefinition_getColorDefinition(this.colorID)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (color: ColorDefinitionDTO) => {
            this.color = color;
            this.updateFromDTO();
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    } else {
      this.color = new ColorDefinitionDTO();
      this.color.color = '#FFFFFF';
      this.loading = false;
    }
  }

  private updateFromDTO() {
    if (this.color) {
      this.Name.setValue(this.color.name);
      this.Name.markAsTouched();
      this.Description.setValue(this.color.description);
      this.Description.markAsTouched();
      this.Color.setValue(this.color.color);
      this.Color.markAsTouched();
    }
  }

  private updateDTOFromForm() {
    if (this.color) {
      this.color.name = this.Name.value;
      this.color.description = this.Description.value;
    }
  }

  cancel() {
    this.returnToColorPage();
  }

  returnToColorPage() {
    this.util.returnToNavigation(this.util.RoutingID.colors);
  }

  submit() {
    this.loading = true;
    this.updateDTOFromForm();
    if (this.colorID) {
      this.api
        .shared_ColorDefinition_editColorDefinition(this.color)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.returnToColorPage();
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    } else {
      this.api
        .shared_ColorDefinition_createColorDefinition(this.color)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.returnToColorPage();
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    }
  }

  setColor($event: string) {
    if ($event) {
      const temp = $event.split('#');
      this.color.color = temp[temp.length - 1];
      this.Color.setValue(this.color.color);
    }
  }
}
